import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeProvider } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import DainTheme from '../DainTheme';
import { useGetMediaSourcesSummary, useGetSourceRuns } from '../api/MonitoringApi';
import RoutePaths, { ALERTS_MONITORING_ID, ALL_SOURCES_MONITORING_ID } from '../routes/RoutePaths';
import DisabledSourceChip from './DisabledSourceChip';
import {
    MONITORING_CONTENT_CONTAINER_SX,
    MONITORING_HEADER_CONTAINER_SX,
} from './monitoringLayout';
import SourceRunsTable from './SourceRunsTable';

function isListMediaId(mediaId) {
    return mediaId === ALL_SOURCES_MONITORING_ID || mediaId === ALERTS_MONITORING_ID;
}

function getMediaDetailPath(mediaId) {
    if (mediaId === ALL_SOURCES_MONITORING_ID) {
        return RoutePaths.monitoring.allSources;
    }
    if (mediaId === ALERTS_MONITORING_ID) {
        return RoutePaths.monitoring.alerts;
    }
    return RoutePaths.monitoring.media.replace(':mediaId', encodeURIComponent(mediaId));
}

export default function SourceDetail() {
    const intl = useIntl();
    const navigate = useNavigate();
    const { mediaId, sourceId } = useParams();
    const mediaQuery = useGetMediaSourcesSummary({
        mediaId,
        enabled: Boolean(mediaId) && !isListMediaId(mediaId),
    });
    const runsQuery = useGetSourceRuns({ sourceId, days: 14 });

    const payload = runsQuery?.data?.result ?? null;
    const runs = payload?.runs ?? [];
    const sourceFromMedia = (mediaQuery?.data?.result?.sources ?? []).find(
        (source) => source.source_id === sourceId
    );
    const isDisabled =
        sourceFromMedia?.enabled === false || payload?.enabled === false;

    return (
        <ThemeProvider theme={DainTheme.light}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        flex: '0 0 auto',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                    }}
                >
                    <Container maxWidth={false} disableGutters sx={MONITORING_HEADER_CONTAINER_SX}>
                        <Stack alignItems="center" spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}
                                >
                                    {sourceId}
                                </Typography>
                                {isDisabled && <DisabledSourceChip />}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {intl.formatMessage({ id: 'monitoring.source.subtitle' })}
                            </Typography>
                        </Stack>
                    </Container>
                </Box>

                <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={MONITORING_CONTENT_CONTAINER_SX}
                    >
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(getMediaDetailPath(mediaId))}
                            sx={{ mb: 1, flexShrink: 0, alignSelf: 'flex-start' }}
                        >
                            {intl.formatMessage({ id: 'monitoring.source.back' })}
                        </Button>

                        {runsQuery.isLoading && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.loading' })}
                            </Typography>
                        )}
                        {runsQuery.isError && (
                            <Typography variant="body2" color="error" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.source.error' })}
                            </Typography>
                        )}
                        {!runsQuery.isLoading && !runsQuery.isError && runs.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.source.empty' })}
                            </Typography>
                        )}
                        {runs.length > 0 && (
                            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                <SourceRunsTable runs={runs} mediaId={mediaId} sourceId={sourceId} />
                            </Box>
                        )}
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
